// client/src/components/pages/Settings/LexiStarDataExport/exportApi.ts
//
// Client of the per-tenant data export API (ST-00207, m4b contracts §4.3; server
// modules/lexistar/exports, mounted at /api/lexistar/exports). The shapes mirror the server's
// modules/lexistar/exports/types.ts. Thin wrapper over the shared axios instance
// (services/api: cookie session, CSRF header, 401 refresh; `api` already carries /api).
//
//   listExports()            GET    /lexistar/exports            -> { jobs, total, datasets, limits }
//   requestExport(body)      POST   /lexistar/exports            -> { job, notes }  (202)
//   requestTruckHistory(t)   POST   /lexistar/exports {asset_id} -> { job, notes } — ST-00289: one
//                                   truck's history (the transfer dialog's download)
//   getExport(id, tenant?)   GET    /lexistar/exports/:id        -> job
//   downloadLink(id, tenant?) GET   /lexistar/exports/:id/download?redirect=false
//                                   -> { url, expires_at, filename } — a one-time link
//   (tenant: a global admin acting for the organisation that owns the export)
//   cancelExport(id)         DELETE /lexistar/exports/:id        -> job
//   startDownload(url, name) sends the browser to the link (an anchor: no blob in memory)
//   errorText(err, fallback) the server's message, else the fallback

import api from '../../../../services/api';

export const DATASET_KEYS = [
  'assets',
  'drivers',
  'driver_consents',
  'driver_asset_allocations',
  'lanes',
  'routes',
  'geofences',
  'trips',
  'trip_legs',
  'trip_timeline',
  'deviations',
  'runs',
  'events',
  'messages',
  'cost_ledger',
  'positions',
  'usage_statements',
  'report_runs',
] as const;

export type DatasetKey = (typeof DATASET_KEYS)[number];

export type ExportState = 'queued' | 'running' | 'done' | 'failed' | 'expired';

export interface ExportProgress {
  phase?: 'starting' | 'exporting' | 'finishing' | 'done' | 'failed';
  dataset?: DatasetKey;
  file?: string;
  files_done?: number;
  files_total?: number;
  rows?: number;
  file_rows?: number;
  bytes?: number;
  restarts?: number;
  updated_at?: string;
}

export interface ExportJob {
  id: string;
  tenant_id: number;
  requested_by: string;
  requested_at: string;
  from_date: string;
  to_date: string;
  scope: DatasetKey[];
  state: ExportState;
  progress: ExportProgress;
  size_bytes: number | null;
  row_counts: Record<string, number> | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  expires_at: string | null;
  downloaded_at: string | null;
  downloadable: boolean;
  /** ST-00289: the one truck of a truck-history export; null / absent = the whole organisation. */
  asset_id?: string | null;
}

export interface DatasetInfo {
  key: DatasetKey;
  label: string;
  description: string;
  dated: boolean;
  personal: boolean;
  files: string[];
  available: boolean;
}

export interface ExportLimits {
  positions_max_days: number;
  positions_max_rows: number;
  default_days: number;
  retention_days: number;
  link_ttl_s: number;
}

export interface ExportList {
  jobs: ExportJob[];
  total: number;
  datasets: DatasetInfo[];
  limits: ExportLimits;
}

export interface ExportRequest {
  from_date: string;
  to_date: string;
  scope: DatasetKey[];
}

export interface ExportRequestResult {
  job: ExportJob;
  notes: string[];
}

export interface DownloadLink {
  url: string;
  expires_at: string;
  filename: string;
}

/** The contract limits, used until the server's answer arrives. */
export const DEFAULT_LIMITS: ExportLimits = {
  positions_max_days: 31,
  positions_max_rows: 5_000_000,
  default_days: 90,
  retention_days: 7,
  link_ttl_s: 86_400,
};

const BASE = '/lexistar/exports';
type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);

export async function listExports(): Promise<ExportList> {
  const { data } = await api.get(BASE, { params: { limit: 50 } });
  const body = isObj(data) ? data : {};
  return {
    jobs: Array.isArray(body.jobs) ? (body.jobs as ExportJob[]) : [],
    total: typeof body.total === 'number' ? body.total : 0,
    datasets: Array.isArray(body.datasets) ? (body.datasets as DatasetInfo[]) : [],
    limits: isObj(body.limits) ? { ...DEFAULT_LIMITS, ...(body.limits as Partial<ExportLimits>) } : DEFAULT_LIMITS,
  };
}

export async function requestExport(body: ExportRequest): Promise<ExportRequestResult> {
  const { data } = await api.post(BASE, body);
  const res = (isObj(data) ? data : {}) as Partial<ExportRequestResult>;
  return { job: res.job as ExportJob, notes: Array.isArray(res.notes) ? res.notes : [] };
}

/**
 * ST-00289: one truck's history — the organisation's own trips, events, positions and so on of
 * that truck while it owned it (the server picks the datasets and the dates). `tenantId` is the
 * organisation that owns the truck (a global admin acts for it; anyone else names their own).
 */
export async function requestTruckHistory({ assetId, tenantId }: { assetId: string; tenantId?: number | null }): Promise<ExportRequestResult> {
  const body: Record<string, unknown> = { asset_id: assetId };
  if (tenantId != null) body.tenant_id = tenantId;
  const { data } = await api.post(BASE, body);
  const res = (isObj(data) ? data : {}) as Partial<ExportRequestResult>;
  return { job: res.job as ExportJob, notes: Array.isArray(res.notes) ? res.notes : [] };
}

/** `tenant_id` only when given (a global admin reading another organisation's export). */
const tenantParams = (tenantId?: number | null): Record<string, number> => (tenantId != null ? { tenant_id: tenantId } : {});

export async function getExport(id: string, tenantId?: number | null): Promise<ExportJob> {
  const url = `${BASE}/${encodeURIComponent(id)}`;
  const { data } = tenantId != null ? await api.get(url, { params: tenantParams(tenantId) }) : await api.get(url);
  return (isObj(data) && isObj(data.job) ? data.job : data) as ExportJob;
}

export async function downloadLink(id: string, tenantId?: number | null): Promise<DownloadLink> {
  const { data } = await api.get(`${BASE}/${encodeURIComponent(id)}/download`, {
    params: { redirect: false, ...tenantParams(tenantId) },
    headers: { Accept: 'application/json' },
  });
  return data as DownloadLink;
}

export async function cancelExport(id: string): Promise<ExportJob> {
  const { data } = await api.delete(`${BASE}/${encodeURIComponent(id)}`);
  return (isObj(data) && isObj(data.job) ? data.job : data) as ExportJob;
}

/** The API path of a one-time link (`/api/...`) on the API's origin. */
export function absoluteUrl(path: string): string {
  const base = String(api.defaults.baseURL || '/api');
  try {
    const origin = new URL(base, window.location.origin).origin;
    return new URL(path, origin).toString();
  } catch {
    return path;
  }
}

/** Send the browser to a download (an anchor: the file streams to disk, not into memory). */
export function startDownload(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: Obj } } | null;
  const d = e?.response?.data;
  const msg = d && (typeof d.error === 'string' ? d.error : typeof d.message === 'string' ? d.message : null);
  if (msg) return msg;
  if (e?.response?.status === 403) return 'You do not have permission to export data.';
  return fallback;
}
